// src/components/dashboard/KnowledgeBase.jsx
import { useState, useMemo } from 'react';

const RESPONSIVE_CSS = `
  .kb-faq-layout { display: flex; gap: 20px; align-items: flex-start; }
  .kb-topic-sidebar { width: 180px; flex-shrink: 0; display: flex; flex-direction: column; gap: 4px; }
  .kb-topic-content { flex: 1; }
  @media (max-width: 640px) {
    .kb-faq-layout { flex-direction: column; }
    .kb-topic-sidebar { width: 100%; flex-direction: row; overflow-x: auto; padding-bottom: 4px; gap: 6px; flex-wrap: nowrap; }
    .kb-topic-sidebar button { white-space: nowrap; flex-shrink: 0; }
    .kb-resource-grid { grid-template-columns: 1fr !important; }
    .kb-stats-row { flex-direction: column; }
  }
`;

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB', white: '#FFFFFF',
  green: '#2E7D32', greenBg: '#e8f5e9', blue: '#1565C0', blueBg: '#e3f2fd',
};

// ─── FAQ CONTENT ────────────────────────────────────────────────────────────

const FAQ_TOPICS = [
  {
    id: 'overview',
    label: 'Program Overview',
    icon: '🏛️',
    questions: [
      {
        q: 'What is the CDBG program?',
        a: 'The Community Development Block Grant (CDBG) program is a federal program administered by the U.S. Department of Housing and Urban Development (HUD). It provides annual grants to states and localities to fund a wide range of community development activities — primarily for low- and moderate-income persons. In Nebraska, the non-entitlement CDBG program is administered by the Nebraska Department of Economic Development (DED).',
      },
      {
        q: 'Who administers CDBG in Nebraska?',
        a: 'The Nebraska Department of Economic Development (DED) administers the state CDBG program for non-entitlement communities — that is, cities and counties not large enough to receive CDBG funds directly from HUD. PADD (Panhandle Area Development District) serves as a regional intermediary, helping Panhandle municipalities prepare and submit applications to DED and manage funded projects.',
      },
      {
        q: 'Which communities are eligible to apply?',
        a: 'Nebraska non-entitlement communities — cities, villages, and counties that do not receive CDBG directly from HUD — are eligible to apply through the state program. All 11 counties and their municipalities in the Nebraska Panhandle are non-entitlement and therefore eligible. Lincoln and Omaha receive their own CDBG allocations directly from HUD and cannot apply through the state.',
      },
      {
        q: 'What types of activities are eligible for CDBG funding?',
        a: 'Eligible activities include: public infrastructure improvements (water, sewer, streets, drainage), public facilities (community centers, fire stations, libraries), housing rehabilitation, economic development and business assistance, planning and capacity building, and acquisition of real property. All activities must meet one of the three national objectives and must not be for the general conduct of government.',
      },
      {
        q: 'What is the typical grant size?',
        a: 'Nebraska CDBG grants vary by project type and available funding. Infrastructure and public facility projects commonly range from $250,000 to $750,000. Housing rehabilitation programs are often in the $300,000–$500,000 range. Economic development grants vary widely depending on job creation and leverage. Contact PADD staff for current funding round limits and priorities.',
      },
      {
        q: 'Is there a local match requirement?',
        a: 'Nebraska CDBG does not have a mandatory match requirement, but local match is encouraged and may strengthen a competitive application. Certain project types — particularly economic development — benefit significantly from demonstrated local investment and private leverage.',
      },
    ],
  },
  {
    id: 'national-objectives',
    label: 'National Objectives',
    icon: '🎯',
    questions: [
      {
        q: 'What are the CDBG national objectives?',
        a: 'Every CDBG-funded activity must meet one of three national objectives: (1) Benefit low- and moderate-income (LMI) persons, (2) Aid in the prevention or elimination of slums or blight, or (3) Meet an urgent community development need. The vast majority of Nebraska CDBG activities qualify under the LMI benefit objective.',
      },
      {
        q: 'What does "low- and moderate-income" mean?',
        a: 'LMI refers to persons whose household income does not exceed 80% of the area median income (AMI), adjusted for family size. HUD publishes income limits annually for each county. For CDBG purposes, "low income" is at or below 50% AMI and "moderate income" is 51–80% AMI.',
      },
      {
        q: 'What is "area benefit" and how does it work?',
        a: 'Area benefit is the most common LMI qualification. An activity qualifies if it serves an area where at least 51% of residents are LMI. The service area is documented using HUD-provided census data (LMISD) or a local income survey. Infrastructure projects (water lines, streets) often qualify this way.',
      },
      {
        q: 'What is the "limited clientele" category?',
        a: 'Some activities qualify because they exclusively serve a clientele that is presumed or documented to be LMI — such as the elderly, persons with disabilities, homeless persons, migrant farmworkers, or abused children. Alternatively, income documentation from each beneficiary can establish LMI status.',
      },
      {
        q: 'What is the "slums and blight" objective?',
        a: 'An activity qualifies under slum and blight if it addresses conditions in a designated slum/blight area or eliminates specific conditions of blight (e.g., demolition of a dangerous structure). This objective is less commonly used in Nebraska but may apply in communities with formally documented blighted areas.',
      },
      {
        q: 'What is the "urgent need" objective?',
        a: 'Urgent need applies when an activity responds to a recent disaster, threat to health/safety, or other emergency that the community cannot finance on its own. This objective requires a specific written determination and is rarely used outside of disaster response situations. CDBG-CV funding was distributed under this authority.',
      },
    ],
  },
  {
    id: 'application',
    label: 'Application Process',
    icon: '📋',
    questions: [
      {
        q: 'How does the CDBG application process work in Nebraska?',
        a: 'Nebraska DED announces CDBG funding rounds with specific deadlines. Communities work with PADD to prepare their applications, which include a project description, needs documentation, national objective qualification, budget, and citizen participation documentation. PADD submits the application to DED on behalf of the municipality.',
      },
      {
        q: 'What is a Needs Assessment and why is it required?',
        a: 'A needs assessment documents that the proposed activity addresses a genuine unmet community need — typically through engineering reports, condition surveys, water/sewer system assessments, or housing condition surveys. DED requires this documentation to verify the project is necessary and appropriately sized.',
      },
      {
        q: 'What is citizen participation and what does it require?',
        a: 'Federal law requires communities to provide citizens an opportunity to comment on CDBG applications and funded activities. This typically means holding at least one public hearing before submitting the application and another before completing the project. Hearings must be advertised in advance, and comments must be documented and addressed.',
      },
      {
        q: 'What is a Statement of Assurances?',
        a: 'The Statement of Assurances (Exhibit C1) is a certification signed by the authorized local official affirming the community\'s commitment to comply with all applicable federal requirements — civil rights laws, environmental review, fair housing, labor standards, and financial management. It is a required application exhibit.',
      },
      {
        q: 'How long does the application and award process take?',
        a: 'From application submission to grant award typically takes 3–6 months, depending on DED\'s review capacity and completeness of the application. Once awarded, execution of the grant agreement takes additional weeks. Plan accordingly — CDBG projects should not be advertised for bids until the grant agreement is fully executed and the environmental review is complete.',
      },
      {
        q: 'Can a community have more than one open CDBG grant at a time?',
        a: 'Nebraska DED generally limits communities to one open CDBG grant at a time. A community must substantially complete and close out an existing grant before a new award will be made. Contact PADD if your community has an older open grant — resolving it should be a priority.',
      },
    ],
  },
  {
    id: 'environmental',
    label: 'Environmental Review',
    icon: '🌿',
    questions: [
      {
        q: 'What is an environmental review and why is it required?',
        a: 'Federal law (NEPA and related statutes) requires an environmental review before CDBG funds can be committed to or expended on a project. The review evaluates potential impacts on the natural and human environment — including floodplains, wetlands, historic properties, noise, air quality, and hazardous materials. No project costs may be incurred until the review is complete and approved.',
      },
      {
        q: 'Who is responsible for the environmental review?',
        a: 'Under CDBG, the responsibility for environmental review rests with the Responsible Entity (RE) — the unit of general local government receiving the grant. The RE cannot delegate this responsibility to a contractor or consultant, though consultants may assist in preparing the review documents. PADD assists municipalities in completing the review.',
      },
      {
        q: 'What are the categories of environmental review?',
        a: 'Activities fall into four categories: (1) Exempt — no review required (e.g., planning studies); (2) Categorically Excluded Not Subject to 58.5 (CENST) — minor documentation; (3) Categorically Excluded Subject to 58.5 (CEST) — requires checking statutory requirements; (4) Environmental Assessment (EA) — full review with public comment. Most Nebraska infrastructure projects are CEST.',
      },
      {
        q: 'What is a Request for Release of Funds (RROF)?',
        a: 'For activities requiring a CEST or EA review, the RE must submit a Request for Release of Funds and Certification to DED. DED then issues an Authority to Use Grant Funds (AUGF). No project costs — including design, engineering, or construction — can be incurred before receiving the AUGF. Incurring costs before AUGF is a serious compliance violation.',
      },
      {
        q: 'What is the "7-day/15-day" rule?',
        a: 'For CEST activities, once the environmental review record is complete, there is a 7-day public comment period followed by a 15-day objection period after DED receives the RROF. The AUGF cannot be issued until these periods pass with no unresolved objections. Total minimum timeline from completed ERR to AUGF is approximately 25 days.',
      },
      {
        q: 'Can design or engineering work begin before the environmental review?',
        a: 'Preliminary engineering and design feasibility studies may be conducted before the environmental review, but costs cannot be paid with CDBG funds unless the AUGF has been issued. Final design, bidding, and construction cannot begin until the AUGF is in hand. Violating this rule can result in disallowance of costs and repayment to DED.',
      },
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement & Contracting',
    icon: '📄',
    questions: [
      {
        q: 'What procurement standards apply to CDBG projects?',
        a: 'CDBG recipients must follow 2 CFR Part 200 Uniform Guidance procurement standards. This requires full and open competition, written procurement procedures, conflict of interest policies, and proper documentation. State and local procurement rules also apply, but federal requirements take precedence where stricter.',
      },
      {
        q: 'When is competitive bidding required?',
        a: 'Sealed competitive bidding (formal advertising) is required for construction contracts exceeding $250,000 (the federal simplified acquisition threshold for construction). For smaller amounts, competitive proposals or quotations may be used. Sole-source procurement is only allowable in limited circumstances and requires prior written justification and approval.',
      },
      {
        q: 'Does the Davis-Bacon Act apply to CDBG projects?',
        a: 'Yes, Davis-Bacon and Related Acts (DBRA) apply to all CDBG-assisted construction contracts over $2,000. Contractors and subcontractors must pay workers the prevailing wage rates determined by the U.S. Department of Labor for the specific type of work and location. Wage determinations must be incorporated into bid documents and contracts. Weekly certified payrolls must be submitted and reviewed.',
      },
      {
        q: 'What required provisions must be in CDBG construction contracts?',
        a: 'CDBG construction contracts must include: Davis-Bacon wage rates and payroll requirements, Section 3 clause, Equal Employment Opportunity clause, Copeland Anti-Kickback Act provisions, Contract Work Hours and Safety Standards Act provisions, termination clauses, and prohibition on use of debarred contractors. PADD can provide a compliant contract template.',
      },
      {
        q: 'What is Section 3 and how does it apply?',
        a: 'Section 3 of the HUD Act of 1968 requires that, to the greatest extent feasible, economic opportunities generated by CDBG-assisted projects be given to low- and very-low income persons and businesses in the area. For construction contracts over $200,000, recipients must document Section 3 efforts. This includes hiring goals and good-faith efforts to recruit Section 3 workers and businesses.',
      },
      {
        q: 'Can the grantee use a contractor it has an existing relationship with?',
        a: 'Conflicts of interest are strictly prohibited. A grantee may not award a contract to a firm in which any covered person (elected official, employee, agent, consultant) has a financial interest or receives a benefit. This applies during the grant and for one year after. Any potential conflict must be disclosed to DED in advance.',
      },
    ],
  },
  {
    id: 'financial',
    label: 'Financial Management',
    icon: '💰',
    questions: [
      {
        q: 'How must CDBG funds be tracked and accounted for?',
        a: 'Grantees must maintain a financial management system that meets 2 CFR Part 200 standards: accurate, current, and complete records; effective internal controls; proper source documentation for all expenditures; and segregation of duties. CDBG funds must be held in a separate, interest-bearing bank account and tracked separately from local funds.',
      },
      {
        q: 'What costs are allowable under CDBG?',
        a: 'Allowable costs must be necessary, reasonable, allocable to the grant, and compliant with 2 CFR Part 200. Eligible project costs include construction, engineering/design, legal fees directly related to the project, environmental review, and grant administration (capped at a percentage of the grant). Costs incurred before the grant agreement or AUGF are generally not allowable.',
      },
      {
        q: 'What is program income and how must it be handled?',
        a: 'Program income is gross income received by the grantee directly generated from CDBG-funded activities — for example, repayments on housing rehab loans, fees from a CDBG-assisted facility, or proceeds from property sales. Program income must be returned to the CDBG program, tracked separately, and used for eligible CDBG activities before drawing additional grant funds.',
      },
      {
        q: 'How does the grantee draw down CDBG funds?',
        a: 'In Nebraska, grantees typically submit reimbursement requests to DED with supporting documentation (invoices, payrolls, contracts). DED reviews the request and releases funds. Requests should be submitted regularly — DED may flag grants with long periods of inactivity. Maintain copies of all supporting documentation for audit purposes.',
      },
      {
        q: 'What is the grant administration cost cap?',
        a: 'Nebraska DED typically limits grant administration costs (staff time, consultant fees for grant management) to a fixed amount or percentage of the total grant. Check your specific grant agreement for the applicable cap. Administration costs must be supported by time sheets or other documentation.',
      },
      {
        q: 'How long must financial records be kept?',
        a: 'CDBG financial records must be retained for the longer of: (1) 3 years after the final drawdown of funds, or (2) resolution of any audit findings or litigation. As a practical matter, most grantees retain records for at least 7 years. Records must be available for inspection by DED, HUD, and the U.S. Inspector General.',
      },
    ],
  },
  {
    id: 'civil-rights',
    label: 'Civil Rights & Fair Housing',
    icon: '⚖️',
    questions: [
      {
        q: 'What civil rights laws apply to CDBG recipients?',
        a: 'CDBG recipients must comply with: Title VI of the Civil Rights Act (no discrimination based on race, color, national origin), Section 504 of the Rehabilitation Act (no discrimination based on disability), the Age Discrimination Act, Title IX, and the Fair Housing Act. These apply to all programs and activities of the grantee, not just the CDBG-funded project.',
      },
      {
        q: 'What is the Fair Housing Act requirement?',
        a: 'CDBG recipients must affirmatively further fair housing (AFFH). This means taking meaningful actions to overcome historic patterns of segregation, promote fair housing choice, and foster inclusive communities. Grantees must also adopt and implement a Residential Anti-Displacement and Relocation Assistance Plan (RADRAP) as a condition of receiving CDBG funds.',
      },
      {
        q: 'What is a Residential Anti-Displacement and Relocation Assistance Plan?',
        a: 'The RADRAP (Exhibit D) is a written plan the community adopts committing to minimize displacement of persons resulting from CDBG-assisted activities and to provide relocation assistance to any persons actually displaced. It must be adopted by the governing body, published, and submitted with the CDBG application.',
      },
      {
        q: 'What is the Limited English Proficiency (LEP) requirement?',
        a: 'Recipients must take reasonable steps to ensure meaningful access to programs and activities for persons with limited English proficiency. This typically involves conducting a Four Factor Analysis (volume of LEP persons, frequency of contact, nature of the program, and resources available) and developing a Language Access Plan if needed. The Four Factor Analysis is Exhibit O1 and the Language Access Plan is Exhibit O2.',
      },
      {
        q: 'What is Section 504 compliance for facilities projects?',
        a: 'Section 504 of the Rehabilitation Act prohibits discrimination based on disability in federally assisted programs. For construction or renovation of public facilities with CDBG funds, the facility must be made fully accessible to persons with disabilities in accordance with applicable accessibility standards (ADA, ABAAS). A Section 504 self-evaluation may be required.',
      },
      {
        q: 'What is the Excessive Force policy requirement?',
        a: 'As a condition of receiving CDBG funds, units of general local government must adopt and enforce a policy prohibiting the use of excessive force by law enforcement agencies against any individuals engaged in non-violent civil rights demonstrations. This is certified in the application (Exhibit) and must be an adopted local policy.',
      },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting & Monitoring',
    icon: '📊',
    questions: [
      {
        q: 'What reports are required during a CDBG grant?',
        a: 'Grantees typically must submit: (1) Regular financial/progress reports to DED on a schedule specified in the grant agreement, (2) Davis-Bacon certified payrolls (weekly during construction), (3) A final performance report at grant completion documenting activities, beneficiaries served, and national objective compliance, and (4) FFATA (Federal Funding Accountability and Transparency Act) report for contracts over $30,000.',
      },
      {
        q: 'What is FFATA reporting?',
        a: 'The Federal Funding Accountability and Transparency Act requires reporting of first-tier subawards (contracts) of $30,000 or more. CDBG grantees must report contracts at or above this threshold on USASpending.gov. PADD assists with this requirement. The FFATA Form (Exhibit L) documents the required information for the primary contract.',
      },
      {
        q: 'What happens during a DED monitoring visit?',
        a: 'DED conducts monitoring visits (in-person or remote) to verify compliance with grant requirements. Monitors will review financial records, procurement files, Davis-Bacon payrolls, environmental review records, civil rights compliance, and progress toward national objective. Having organized, complete files is essential. PADD can conduct a pre-monitoring review to help you prepare.',
      },
      {
        q: 'How should certified payrolls be reviewed?',
        a: 'The grantee (or PADD on its behalf) must review weekly certified payrolls submitted by the prime contractor and all subcontractors for Davis-Bacon compliance. Review for: correct wage rates by classification, overtime calculation, required fringe benefits, and completeness of certifications. Any underpayments must be corrected promptly and documented.',
      },
      {
        q: 'What is beneficiary documentation and why does it matter?',
        a: 'Documenting who benefits from CDBG activities is essential to proving national objective compliance. For LMI area benefit, census/survey data establishes the percentage of LMI residents in the service area. For limited clientele, income surveys or presumption documentation for each beneficiary household is required. Inadequate beneficiary documentation is one of the most common audit findings.',
      },
      {
        q: 'How long must CDBG project records be kept?',
        a: 'All CDBG records — financial, procurement, environmental, civil rights, beneficiary, and program — must be retained for a minimum of 3 years after the final grant closeout or resolution of any audit findings. In practice, 7 years is recommended. Records include digital files, contracts, invoices, payrolls, meeting minutes, and correspondence.',
      },
    ],
  },
  {
    id: 'closeout',
    label: 'Closeout',
    icon: '✅',
    questions: [
      {
        q: 'When can a CDBG grant be closed out?',
        a: 'A grant is ready for closeout when: all project activities are physically complete, all costs have been paid and reimbursed, all required reports have been submitted, national objective compliance is documented, all Davis-Bacon issues are resolved, and any monitoring findings have been corrected. DED initiates the formal closeout process.',
      },
      {
        q: 'What is required for formal grant closeout?',
        a: 'Closeout typically requires submission of a final performance report, final financial reconciliation, documentation of national objective compliance, resolution of any outstanding issues or audit findings, and return of any unexpended CDBG funds. DED will issue a formal closeout letter confirming the grant is closed.',
      },
      {
        q: 'What happens to program income after closeout?',
        a: 'Program income generated after closeout (e.g., ongoing loan repayments from a housing rehab revolving loan fund) must continue to be tracked and used for eligible CDBG activities as specified in the closeout agreement. This obligation does not end at grant closeout — it continues as long as the community receives program income from CDBG-assisted activities.',
      },
      {
        q: 'Can we apply for a new CDBG grant before our current one is closed?',
        a: 'Nebraska DED generally requires substantial completion and closeout of an existing grant before awarding a new one to the same community. If your community has an open grant that is complete, contact PADD immediately to begin the closeout process — do not let closeout drag on, as it affects your eligibility for future funding.',
      },
      {
        q: 'What if there are unexpended funds at closeout?',
        a: 'Any CDBG funds that were drawn down but not spent on allowable activities must be returned to DED. Funds that were never drawn down are simply de-obligated. If a project came in under budget, the unexpended grant balance reverts to DED for redistribution. You cannot use leftover CDBG funds for other local purposes.',
      },
    ],
  },
];

// ─── RESOURCE LIBRARY ───────────────────────────────────────────────────────

const TYPE_BADGE = {
  PDF:   { background: '#fde8e8', color: '#991b1b' },
  DOCX:  { background: '#e3f2fd', color: '#1565C0' },
  XLSX:  { background: '#e8f5e9', color: '#2E7D32' },
  Web:   { background: C.cream,   color: C.slate   },
  Video: { background: '#fff3e0', color: '#e65100' },
};

// type values: 'PDF' | 'DOCX' | 'XLSX' | 'Web' | 'Video'
const RESOURCES = [
  // ── Nebraska DED ──────────────────────────────────────────────────────────
  {
    icon: '🏛️', category: 'Nebraska DED', type: 'Web',
    title: 'Nebraska CDBG Program Page',
    desc: 'DED program page with current funding round information, application materials, and state-specific guidance.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '📘', category: 'Nebraska DED', type: 'PDF',
    title: 'Nebraska CDBG Administrative Manual',
    desc: 'The complete state administrative manual covering all program requirements for Nebraska CDBG grantees.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '📋', category: 'Nebraska DED', type: 'PDF',
    title: 'CDBG Application & Pre-Application Forms',
    desc: 'Current application forms, pre-application questionnaire, and submission instructions from Nebraska DED.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '✅', category: 'Nebraska DED', type: 'PDF',
    title: 'Nebraska CDBG Grant Compliance Checklist',
    desc: 'State-level compliance checklist covering all required exhibits and documentation for grant management.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '📢', category: 'Nebraska DED', type: 'PDF',
    title: 'Notice of Funding Opportunity (NOFO)',
    desc: 'Current funding round announcement with priorities, thresholds, and deadlines for Nebraska CDBG applications.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '🗂️', category: 'Nebraska DED', type: 'DOCX',
    title: 'CDBG Activity Delivery Cost Form',
    desc: 'Required form for documenting and justifying activity delivery costs charged to a CDBG grant.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },

  // ── Federal Regulations ───────────────────────────────────────────────────
  {
    icon: '⚖️', category: 'Federal Regulations', type: 'Web',
    title: '24 CFR Part 570 — CDBG Regulations',
    desc: 'The full federal regulation governing the CDBG program, including eligible activities and national objectives.',
    url: 'https://www.ecfr.gov/current/title-24/subtitle-B/chapter-V/subchapter-C/part-570',
  },
  {
    icon: '📜', category: 'Federal Regulations', type: 'Web',
    title: '2 CFR Part 200 — Uniform Guidance',
    desc: 'Federal cost principles and administrative requirements governing all CDBG financial management and procurement.',
    url: 'https://www.ecfr.gov/current/title-2/subtitle-A/chapter-II/part-200',
  },
  {
    icon: '📗', category: 'Federal Regulations', type: 'PDF',
    title: 'CDBG National Objectives Guide (HUD)',
    desc: 'HUD guidance document explaining how to qualify activities under LMI benefit, slum/blight, and urgent need.',
    url: 'https://www.hudexchange.info/programs/cdbg/',
  },
  {
    icon: '💻', category: 'Federal Regulations', type: 'Web',
    title: 'HUD Exchange — CDBG Resource Hub',
    desc: 'Central hub for CDBG guidance documents, training materials, Q&As, waivers, and program updates.',
    url: 'https://www.hudexchange.info/programs/cdbg/',
  },

  // ── Environmental Review ──────────────────────────────────────────────────
  {
    icon: '🌿', category: 'Environmental Review', type: 'Web',
    title: '24 CFR Part 58 — Environmental Review',
    desc: 'Federal regulation establishing Responsible Entity environmental review procedures for CDBG projects.',
    url: 'https://www.ecfr.gov/current/title-24/subtitle-A/part-58',
  },
  {
    icon: '📙', category: 'Environmental Review', type: 'PDF',
    title: 'HUD Environmental Review Handbook',
    desc: 'Comprehensive HUD handbook covering the environmental review process, tiering, and record-keeping requirements.',
    url: 'https://www.hudexchange.info/programs/environmental-review/',
  },
  {
    icon: '📄', category: 'Environmental Review', type: 'DOCX',
    title: 'Sample Environmental Review Record (ERR)',
    desc: 'Template ERR with all required statutory checklists, NEPA findings, and public notice documentation.',
    url: 'https://www.hudexchange.info/programs/environmental-review/',
  },
  {
    icon: '🔍', category: 'Environmental Review', type: 'Web',
    title: 'HUD Environmental Review Tools (ER.gov)',
    desc: 'Online tools for completing environmental reviews, including geospatial mapping and compliance screens.',
    url: 'https://www.hudexchange.info/programs/environmental-review/',
  },

  // ── Labor Standards ───────────────────────────────────────────────────────
  {
    icon: '💵', category: 'Labor Standards', type: 'Web',
    title: 'Davis-Bacon Wage Determinations (SAM.gov)',
    desc: 'Search current prevailing wage determinations by county and work classification for all CDBG construction contracts.',
    url: 'https://sam.gov/content/wage-determinations',
  },
  {
    icon: '📑', category: 'Labor Standards', type: 'PDF',
    title: 'HUD Labor Standards Desk Guide',
    desc: 'HUD Labor Relations guide covering contractor requirements, certified payroll review, interviews, and enforcement.',
    url: 'https://www.hud.gov/program_offices/davis_bacon_and_labor_standards',
  },
  {
    icon: '📝', category: 'Labor Standards', type: 'PDF',
    title: 'WH-347 Certified Payroll Form',
    desc: 'DOL standard certified payroll form required weekly from contractors on Davis-Bacon covered CDBG projects.',
    url: 'https://www.dol.gov/sites/dolgov/files/WHD/legacy/files/wh347.pdf',
  },
  {
    icon: '🤝', category: 'Labor Standards', type: 'Web',
    title: 'Section 3 Compliance Resources (HUD)',
    desc: 'Requirements for providing employment and contracting opportunities to low-income residents on CDBG projects.',
    url: 'https://www.hudexchange.info/programs/section-3/',
  },

  // ── Civil Rights & Participation ──────────────────────────────────────────
  {
    icon: '🏠', category: 'Civil Rights & Participation', type: 'Web',
    title: 'Fair Housing Act — HUD Resources',
    desc: 'HUD guidance on Fair Housing obligations, Affirmatively Furthering Fair Housing (AFFH), and complaint procedures.',
    url: 'https://www.hud.gov/program_offices/fair_housing_equal_opp',
  },
  {
    icon: '♿', category: 'Civil Rights & Participation', type: 'Web',
    title: 'Section 504 / ADA Public Facilities Guidance',
    desc: 'Requirements for making CDBG-assisted public facilities accessible to persons with disabilities.',
    url: 'https://www.hud.gov/program_offices/fair_housing_equal_opp/disability_main',
  },
  {
    icon: '🌐', category: 'Civil Rights & Participation', type: 'Web',
    title: 'Limited English Proficiency (LEP) Resources',
    desc: 'HUD guidance on conducting the Four-Factor Analysis and developing a Language Access Plan for your community.',
    url: 'https://www.hud.gov/program_offices/fair_housing_equal_opp/lep',
  },
  {
    icon: '📣', category: 'Civil Rights & Participation', type: 'DOCX',
    title: 'Citizen Participation Plan Template',
    desc: 'Model Citizen Participation Plan outlining required public notice, comment periods, and hearing procedures.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '📰', category: 'Civil Rights & Participation', type: 'DOCX',
    title: 'Public Notice Templates',
    desc: 'Standard public notice language for grant applications, environmental reviews, and contract advertisements.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },

  // ── Financial & Reporting ─────────────────────────────────────────────────
  {
    icon: '📊', category: 'Financial & Reporting', type: 'Web',
    title: 'HUD Low-Mod Income Summary Data (LMISD)',
    desc: 'Census-based LMI data used to document area benefit national objective qualification. Updated after each census.',
    url: 'https://www.hudexchange.info/programs/acs-low-mod-summary-data/',
  },
  {
    icon: '📈', category: 'Financial & Reporting', type: 'Web',
    title: 'HUD Income Limits (AMI Tables)',
    desc: 'Annual HUD income limit tables by county and family size used to determine LMI household eligibility.',
    url: 'https://www.huduser.gov/portal/datasets/il.html',
  },
  {
    icon: '💻', category: 'Financial & Reporting', type: 'Web',
    title: 'IDIS — Integrated Disbursement & Information System',
    desc: 'HUD\'s online grant management system for drawdowns, activity reporting, and performance tracking.',
    url: 'https://www.hudexchange.info/manage-a-program/idis/',
  },
  {
    icon: '📒', category: 'Financial & Reporting', type: 'PDF',
    title: 'Financial Management Handbook for CPD Grantees',
    desc: 'HUD guide covering allowable costs, internal controls, recordkeeping, and audit requirements for CDBG funds.',
    url: 'https://www.hudexchange.info/programs/cdbg/',
  },

  // ── Procurement ───────────────────────────────────────────────────────────
  {
    icon: '📃', category: 'Procurement', type: 'DOCX',
    title: 'Nebraska CDBG Procurement Policy Template',
    desc: 'Model written procurement policy meeting 2 CFR 200 requirements for Nebraska CDBG grantees.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
  {
    icon: '🏢', category: 'Procurement', type: 'Web',
    title: 'SAM.gov — Debarment & Exclusions Check',
    desc: 'Verify contractors are not debarred or suspended before executing contracts. Required for all CDBG procurement.',
    url: 'https://sam.gov',
  },
  {
    icon: '🗒️', category: 'Procurement', type: 'DOCX',
    title: 'Contract Compliance Monitoring Form',
    desc: 'Form for documenting contractor compliance monitoring visits, including labor standards and equal opportunity.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },

  // ── Training ──────────────────────────────────────────────────────────────
  {
    icon: '🎓', category: 'Training', type: 'Web',
    title: 'HUD Exchange Training Courses (CDBG)',
    desc: 'On-demand and live training modules on national objectives, environmental review, labor standards, and more.',
    url: 'https://www.hudexchange.info/trainings/',
  },
  {
    icon: '🎬', category: 'Training', type: 'Video',
    title: 'On the Road to CDBG Compliance (HUD)',
    desc: 'HUD video training series covering the key compliance requirements for CDBG-funded infrastructure projects.',
    url: 'https://www.hudexchange.info/trainings/',
  },
  {
    icon: '📡', category: 'Training', type: 'Web',
    title: 'Nebraska CDBG Connect — Webinars & Resources',
    desc: 'Nebraska DED webinar recordings, slide decks, and resources from CDBG Connect training sessions.',
    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',
  },
];

// ─── CDBG-CV SECTION ────────────────────────────────────────────────────────

const CDBG_CV = [
  {
    q: 'What is CDBG-CV?',
    a: 'CDBG-CV refers to CDBG Coronavirus funding appropriated under the CARES Act (2020). Congress provided $5 billion in supplemental CDBG funding to states, entitlement communities, and insular areas to prevent, prepare for, and respond to COVID-19. Nebraska received an allocation that was distributed to eligible communities and organizations.',
  },
  {
    q: 'How does CDBG-CV differ from regular CDBG?',
    a: 'CDBG-CV was distributed under the "urgent need" national objective rather than the standard LMI benefit objective. This allowed more flexibility in who could be served. HUD waived or modified several standard CDBG requirements for CV funds, including some citizen participation timelines and the 15% public services cap. Check your specific grant agreement for applicable waivers.',
  },
  {
    q: 'What activities were eligible for CDBG-CV?',
    a: 'Eligible CDBG-CV activities included: medical facility improvements, testing and treatment facilities, food banks, personal protective equipment, economic development assistance to businesses impacted by COVID-19, mortgage/rental assistance programs, and public services for persons affected by the pandemic. Activities had to be related to COVID-19 prevention, preparation, or response.',
  },
  {
    q: 'Are CDBG-CV grants still open?',
    a: 'Many CDBG-CV grants have been completed and closed, but some communities may still have open grants depending on when they were awarded and project complexity. If your community has an open CDBG-CV grant, contact PADD to assess status and proceed with closeout. HUD has set expenditure deadlines for CV funds.',
  },
  {
    q: 'What documentation is required for CDBG-CV closeout?',
    a: 'CDBG-CV closeout requires the same core documentation as regular CDBG: final performance report, financial reconciliation, national objective documentation (urgent need), and resolution of any compliance issues. Additionally, grantees should document the COVID-19 nexus for all funded activities — the connection between the activity and pandemic response must be clear in the record.',
  },
];

// ─── COMPONENT ──────────────────────────────────────────────────────────────

export default function KnowledgeBase() {
  const [activeTopic, setActiveTopic]     = useState('overview');
  const [expandedQ, setExpandedQ]         = useState(null);
  const [search, setSearch]               = useState('');
  const [activeSection, setActiveSection] = useState('faq'); // 'faq' | 'resources' | 'cv'
  const [resourceFilter, setResourceFilter] = useState('All');

  const currentTopic = FAQ_TOPICS.find(t => t.id === activeTopic);

  // Search across all topics
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const results = [];
    for (const topic of FAQ_TOPICS) {
      for (const item of topic.questions) {
        if (item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)) {
          results.push({ ...item, topicLabel: topic.label, topicIcon: topic.icon });
        }
      }
    }
    return results;
  }, [search]);

  const resourceCategories = ['All', ...new Set(RESOURCES.map(r => r.category))];
  const filteredResources = resourceFilter === 'All'
    ? RESOURCES
    : RESOURCES.filter(r => r.category === resourceFilter);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1000, margin: '0 auto', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{RESPONSIVE_CSS}</style>

      {/* ── HEADER ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 22, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>
          CDBG Knowledge Base
        </h2>
        <p style={{ fontSize: 13, color: C.slate, margin: '0 0 20px' }}>
          Program guidance, compliance requirements, and resources for Nebraska CDBG projects.
        </p>

        {/* Section tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: `2px solid ${C.sand}`, paddingBottom: 0 }}>
          {[
            { key: 'faq',       label: '❓ FAQ' },
            { key: 'resources', label: '📚 Resource Library' },
            { key: 'cv',        label: '🦠 CDBG-CV' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              style={{
                padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                background: 'transparent', border: 'none',
                color: activeSection === key ? C.gold : C.slate,
                borderBottom: `3px solid ${activeSection === key ? C.gold : 'transparent'}`,
                marginBottom: -2,
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════ FAQ SECTION ══════════ */}
      {activeSection === 'faq' && (
        <div>
          {/* Search */}
          <div style={{ marginBottom: 20, position: 'relative' }}>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setExpandedQ(null); }}
              placeholder="Search all questions…"
              style={{
                width: '100%', padding: '10px 16px 10px 40px', borderRadius: 10,
                border: `1.5px solid ${C.sand}`, fontSize: 14, outline: 'none',
                fontFamily: 'inherit', color: C.navy, boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = C.gold)}
              onBlur={(e)  => (e.target.style.borderColor = C.sand)}
            />
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: C.slate }}>🔍</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: C.slate }}
              >
                ×
              </button>
            )}
          </div>

          {/* Search results */}
          {searchResults !== null ? (
            <div>
              <p style={{ fontSize: 13, color: C.slate, marginBottom: 12 }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
              </p>
              {searchResults.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: C.slate, background: C.white, borderRadius: 10, border: `1px solid ${C.sand}` }}>
                  No questions match your search. Try different keywords.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {searchResults.map((item, i) => (
                  <div key={i} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.sand}`, overflow: 'hidden' }}>
                    <button
                      onClick={() => setExpandedQ(expandedQ === `s${i}` ? null : `s${i}`)}
                      style={{
                        width: '100%', padding: '14px 18px', background: 'none', border: 'none',
                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
                        textAlign: 'left',
                      }}
                    >
                      <div>
                        <span style={{ fontSize: 11, color: C.gold, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                          {item.topicIcon} {item.topicLabel}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{item.q}</span>
                      </div>
                      <span style={{ color: C.gold, fontSize: 18, flexShrink: 0 }}>{expandedQ === `s${i}` ? '−' : '+'}</span>
                    </button>
                    {expandedQ === `s${i}` && (
                      <div style={{ padding: '0 18px 16px', fontSize: 14, color: C.slate, lineHeight: 1.7 }}>
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Normal topic view */
            <div className="kb-faq-layout">

              {/* Topic sidebar */}
              <div className="kb-topic-sidebar">
                {FAQ_TOPICS.map(topic => (
                  <button
                    key={topic.id}
                    onClick={() => { setActiveTopic(topic.id); setExpandedQ(null); }}
                    style={{
                      padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', textAlign: 'left', border: 'none',
                      background: activeTopic === topic.id ? C.navy : 'transparent',
                      color: activeTopic === topic.id ? C.cream : C.slate,
                      transition: 'all 0.15s',
                    }}
                  >
                    {topic.icon} {topic.label}
                  </button>
                ))}
              </div>

              {/* Questions */}
              <div className="kb-topic-content" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700, color: C.navy, margin: '0 0 12px' }}>
                  {currentTopic?.icon} {currentTopic?.label}
                </h3>
                {currentTopic?.questions.map((item, i) => (
                  <div key={i} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.sand}`, overflow: 'hidden' }}>
                    <button
                      onClick={() => setExpandedQ(expandedQ === i ? null : i)}
                      style={{
                        width: '100%', padding: '14px 18px', background: 'none', border: 'none',
                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        textAlign: 'left', gap: 12,
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{item.q}</span>
                      <span style={{ color: C.gold, fontSize: 18, flexShrink: 0 }}>{expandedQ === i ? '−' : '+'}</span>
                    </button>
                    {expandedQ === i && (
                      <div style={{ padding: '0 18px 16px', fontSize: 14, color: C.slate, lineHeight: 1.7 }}>
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>
      )}

      {/* ══════════ RESOURCE LIBRARY ══════════ */}
      {activeSection === 'resources' && (
        <div>
          {/* Category filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {resourceCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setResourceFilter(cat)}
                style={{
                  padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: resourceFilter === cat ? C.navy : C.white,
                  color: resourceFilter === cat ? C.cream : C.slate,
                  border: `1px solid ${resourceFilter === cat ? C.navy : C.sand}`,
                  transition: 'all 0.15s',
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="kb-resource-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {filteredResources.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    background: C.white, borderRadius: 12, border: `1px solid ${C.sand}`,
                    padding: '20px', height: '100%', cursor: 'pointer', transition: 'all 0.2s', boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = C.gold; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.sand; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ fontSize: 26 }}>{r.icon}</div>
                    {r.type && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: '0.05em',
                        ...TYPE_BADGE[r.type],
                      }}>{r.type}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 6 }}>{r.title}</div>
                  <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5, marginBottom: 12 }}>{r.desc}</div>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                    background: C.cream, color: C.slate, fontSize: 11, fontWeight: 600,
                  }}>
                    {r.category}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ══════════ CDBG-CV SECTION ══════════ */}
      {activeSection === 'cv' && (
        <div>
          <div style={{
            background: '#fff8e1', border: '1px solid #f9a825', borderRadius: 10,
            padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#5d4037',
          }}>
            <strong>CDBG-CV</strong> refers to supplemental CDBG funding appropriated under the CARES Act (2020) for COVID-19 response. Some specific requirements differ from regular CDBG — review your grant agreement for applicable HUD waivers.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CDBG_CV.map((item, i) => (
              <div key={i} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.sand}`, overflow: 'hidden' }}>
                <button
                  onClick={() => setExpandedQ(expandedQ === `cv${i}` ? null : `cv${i}`)}
                  style={{
                    width: '100%', padding: '14px 18px', background: 'none', border: 'none',
                    cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    textAlign: 'left', gap: 12,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{item.q}</span>
                  <span style={{ color: C.gold, fontSize: 18, flexShrink: 0 }}>{expandedQ === `cv${i}` ? '−' : '+'}</span>
                </button>
                {expandedQ === `cv${i}` && (
                  <div style={{ padding: '0 18px 16px', fontSize: 14, color: C.slate, lineHeight: 1.7 }}>
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
